import React, { FC, ReactNode } from 'react';
import Button from '../../../../Button/Button';
import Icon from '../../../../Icon2/Icon';
import Tooltip from '../../../../Tooltip/Tooltip';
import { AISuggestions, AssetValidationFilter } from '../../../utils/types';

export const getTestIdFormatter = (testIdString: string): string => {
  if (testIdString) {
    testIdString = testIdString.trim().split(" ").join("-").toLowerCase();
    return testIdString
  }
  return testIdString
}

export interface AssetData {
  uid: string;
  title: string;
  name?: string;
  description?: string;
  url?: string;
  is_dir: boolean;
  space_uid?: string;
  space_name?: string;
  created_at?: string;
  updated_at?: string;
  tags?: string[];
  asset_type?: {
    title: string;
    [key: string]: any;
  };
  [key: string]: any;
}

export interface ListViewProps {
  assetPrivacyEnabled?: boolean;

  /**
   * Allows multiple assets to be selected
   */
  isMultiple: boolean;

  /**
   * Maximum number of assets that can be selected (when isMultiple is true)
   */
  maxInstance?: number;

  /**
   * Array of currently selected assets
   */
  spaceAssetsPicked: AssetData[];

  /**
   * Function to update selected assets
   */
  setSpaceAssetsPicked: (assets: AssetData[] | ((prev: AssetData[]) => AssetData[])) => void;

  /**
   * Function to handle breadcrumb clicks for folder navigation
   */
  breadcrumbClickHandler: (crumb: any) => void;

  /**
   * Currently selected folder (if any)
   */
  selectedFolder: AssetData | null;

  /**
   * Options for search functionality
   */
  spaceAssetsSearchOptions: {
    searchText: string;
    searchType: string;
  };

  /**
   * Function to set the message component
   */
  setMessageComponent: (component: ReactNode) => void;

  /**
   * Function to set API error state
   */
  setIsAPIError: (isError: boolean) => void;

  /**
   * Flag indicating if there was an API error
   */
  isAPIError: boolean;

  /**
   * Function to clear search filters
   */
  setShouldClearSearchFilters?: (shouldClear: boolean) => void;

  /**
   * Function to handle navigating back
   */
  handleGoBack?: () => void;

  /**
   * List of linked spaces to fetch assets from
   */
  linkedSpaces?: string[];

  /**
   * Function to update boolean state for disabling modal action panel
   */
  setShouldDisabledActionPanel?: (shouldDisable: boolean) => void;

  /**
   * Function to fetch space assets
   */
  fetchSpaceAssets: (params: any, headers: any, options: any) => Promise<any>;

  /** 
   * Number of assets returned from the API / Selected to show on bulk selection
  */
  totalCounts?: number;

  /**
   * Function to set the total count of assets
   */
  setTotalCounts?: (totalCount: number) => void;

  /** 
   * Boolean to show the selected assets in the modal
  */
  showSelectedAssets?: boolean;

  /**
   * Flag indicating if AI suggestions are being displayed
   */
  ai_suggestions?: AISuggestions;

  /**
   * Linked workspaces
   */
  linkedWorkspaces: { workspace: string, space_uid: string }[];

  localesWithFallback: { code: string, name: string }[];

  /**
   * Translation function for i18n
   */
  
  validationFilter?: AssetValidationFilter;

  /**
   * When true, effectively-private assets are shown but disabled (non-selectable).
   */
  disablePrivateAssets?: boolean;

  spacesWithTitles?: Array<{ uid: string; title: string }>;

  /**
   * Whether the modal is in moveTarget mode (folder-only, no selection)
   */
  isMoveTarget?: boolean;

  /**
   * Whether the modal is in uploadTarget mode
   */
  isUploadTarget?: boolean;

  /**
   * Whether the currently viewed folder was just created
   */
  isNewlyCreatedFolder?: boolean;

  /**
   * Function to resolve user display names from UID
   */
  getUserDisplayName?: (uid: string) => string;
  /** Notifies when a Suggestions-mode asset fetch completes (`true` = success). */
  onSuggestionsFetchSettled?: (success: boolean) => void;

  /** Map of user UIDs to display names for resolving created_by / updated_by */
  userDisplayNameMap?: Record<string, string>;

  assetScanEnabled?: boolean;
}

interface TagItemProps {
  items: string[];
  label: string;
  customClassname?: string;
}

interface TagItem2Props {
  items: Array<{ name: string; color?: string }>;
  label: string;
}

export interface TagDisplayProps {
  hasNoAccess?: boolean;
  tags?: string[];
  label: string;
  testId?: string;
  type?: 1 | 2;
  customClassname?: string;
  showTagCount?: boolean;
  tagHeaderLabel?: string;
  isRolesColumn?: boolean;
}

/**
 * Initialize the status map for the visible items
 * @param {number} start Start index
 * @param {number} stop Stop index
 * @param {'loading' | 'loaded'} status Status to set
 * @param {Function} setItemStatusMap State setter for item status map
 * @returns {Object} The initialized status map
 */
export const initializeStatusMap = (
  start: number,
  stop: number,
  status: 'loading' | 'loaded',
  setItemStatusMap: React.Dispatch<React.SetStateAction<Record<number, string>>>
): Record<number, string> => {
  const initialMap: Record<number, string> = {};
  for (let i = start; i <= stop; i++) {
    initialMap[i] = status;
  }
  setItemStatusMap(initialMap);
  return initialMap;
};

/**
 * Update the status of specific items
 * @param {number} startIdx Start index
 * @param {number} stopIdx Stop index
 * @param {'loading' | 'loaded'} status Status to set
 * @param {Function} setItemStatusMap State setter for item status map
 */
export const updateItemStatus = (
  startIdx: number,
  stopIdx: number,
  status: 'loading' | 'loaded',
  setItemStatusMap: React.Dispatch<React.SetStateAction<Record<number, string>>>
): void => {
  const statusUpdate: Record<number, string> = {};
  for (let i = startIdx; i <= stopIdx; i++) {
    statusUpdate[i] = status;
  }
  setItemStatusMap(prev => ({ ...prev, ...statusUpdate }));
};

/**
 * Convert table sort format to API sort format
 * @param {Array} tableSortByParam Table sort parameters
 * @returns {Object} Converted sort parameters for API
 */
export const convertSortFormat = (
  tableSortByParam?: Array<{ id: string; desc: boolean }>
): { id: string; sortingDirection: 'asc' | 'desc' } => {
  if (!tableSortByParam || !tableSortByParam.length) {
    return { id: 'title', sortingDirection: 'asc' };
  }
  const sortItem = tableSortByParam[0];
  return {
    id: sortItem.id,
    sortingDirection: sortItem.desc ? 'desc' : 'asc'
  };
};

export const TagItem: FC<TagItemProps> = ({ items, label, customClassname }) => {
  return (
    <div className={`status__tooltip tag ${customClassname ? customClassname : ''}`}>
      <h5 className="tag__head">{label}</h5>
      <div className="tag__body">
        {items &&
          Array.isArray(items) &&
          items.map((eachItem, idx) => {
            return (
              <span key={idx} className="tag__body--item">
                {eachItem}
                {idx !== items.length - 1 ? ',' : ''}
              </span>
            );
          })}
      </div>
    </div>
  );
};

export const TagItem2: FC<TagItem2Props> = ({ items, label }) => {
  return (
    <div className="status__tooltip tag" data-test-id={`cs-${getTestIdFormatter(label)}-info`}>
      <h5 className="tag__head">{label}</h5>
      <div className="tag__body--type2">
        {items && Array.isArray(items) && items.length
          ? items.map((item, idx) => {
            return (
              <div
                key={idx}
                className="tag__item"
                data-test-id={`cs-${getTestIdFormatter(label)}-info-element`}
              >
                {item.color && (
                  <Icon icon="EnvironmentColor" height={8} width={8} fill={item.color} />
                )}
                <div className="item__text">
                  <div className="item__text--env">{item.name}</div>
                </div>
              </div>
            );
          })
          : null}
      </div>
    </div>
  );
};

export const TagDisplay: FC<TagDisplayProps> = ({
  hasNoAccess,
  tags = [],
  label,
  testId,
  type,
  customClassname,
  showTagCount = true,
  isRolesColumn = false,
}) => {
  if (hasNoAccess) {
    return (
      <div
        className="status__container status__container-noAccess "
        data-test-id={testId || 'cs-tag-display'}
      >
        <span className="status__container--label">No read access</span>
        <Icon icon="Docs" height={18} width={18} className="status__icon"></Icon>
      </div>
    );
  }

  if (!tags || !Array.isArray(tags) || tags.length === 0) {
    return (
      <div className="noStatus" data-test-id={testId || 'cs-tag-display'}>
        -
      </div>
    );
  }

  const finalLabel = tags.length > 1 ? `${label}s` : label;

  const controlledTooltipProps = {
    variantType: 'basic' as const,
    position: 'right' as const,
    interactive: true,
  };

  const finalTags = tags;
  const finalTagsSliced = finalTags.slice(1, finalTags.length);

  return (
    <div className="status__container" data-test-id={testId || 'cs-tag-display'}>
      <span className="status__container--label">
        {!isRolesColumn && showTagCount && tags.length}{' '}
        {isRolesColumn ? finalTags[0] : finalLabel}
      </span>
      <Tooltip
        appendTo={document.body}
        maxWidth={440}
        content={
          type === 2 ? (
            <TagItem2
              items={(isRolesColumn ? finalTagsSliced : tags).map(tag =>
                typeof tag === 'string' ? { name: tag } : tag
              )}
              label={finalLabel}
            />
          ) : (
            <TagItem
              items={isRolesColumn ? finalTagsSliced : tags as string[]}
              label={finalLabel}
              customClassname={customClassname}
            />
          )
        }
        {...controlledTooltipProps}
      >
        {isRolesColumn ? (
          tags.length > 1 ? (
            <span
              className="status__container--more"
              data-test-id="cms-change-menu-roles-show-more-roles"
            >{`+${tags.length - 1} more`}</span>
          ) : null
        ) : (
          <Button
            version="v2"
            buttonType="tertiary"
            className="status__icon"
            testId={testId ? `${testId}-Info-icon` : 'cs-asset-tag-Info-icon'}
            icon="v2-Information"
            onlyIcon={true}
            onlyIconHoverColor="secondary"
          />
        )}
      </Tooltip>
    </div>
  );
};